/**
 * Checking-mode role simulation (KEY-498), client side.
 *
 * Checking makes every checker an Admin on the test dial — which is why a checking dial is the one place
 * the per-status `allowedRoles`, the action matrix and the per-attribute rules can't actually be tried out.
 * The toggle asks the server to evaluate the caller as one other role instead; this module is the single
 * place the client's own mirror of that decision lives, so no screen can drift from it.
 *
 * The server is the real gate, and it prunes what it will not show — restricted attribute values, restricted
 * messages, the documents behind them — before the payload is built. This is only about which affordances
 * the UI offers.
 */

/** The roles a checker may look at the dial as. Exactly the flags `InstanceRole` declares. */
export const SIMULATABLE_ROLES = ["Admin", "Editor", "Reviewer", "Reader"] as const;

export type SimulatableRole = (typeof SIMULATABLE_ROLES)[number];

export function isSimulatableRole(value: string | null | undefined): value is SimulatableRole {
  return !!value && (SIMULATABLE_ROLES as readonly string[]).includes(value);
}

/**
 * The role set every affordance on the dial should be judged against.
 *
 * While a simulation is in force it is EXACTLY the one simulated flag — not a ladder, and not a union with
 * the caller's real grants. "Editor" means `{Editor}`: if this dial's statuses only admit a Reviewer, a
 * simulated Editor gets a read-only dial, and that is the correct answer rather than a bug. `managesInstances`
 * is ignored for the same reason — the org capability is exactly the Admin standing being set aside.
 *
 * With nothing simulated it is the caller's own union: Admin for an org instance-manager, otherwise every
 * grant they hold at the scope in question.
 *
 * @param realRoles the caller's own roles at the scope being judged
 * @param managesInstances whether they hold the org-wide `instance.manage` capability
 * @param simulatedRole the role the SERVER reported it evaluated as (`InstanceDetail.simulatedRole`), or null
 */
export function viewRoles(
  realRoles: Iterable<string>,
  managesInstances: boolean,
  simulatedRole: string | null | undefined,
): Set<string> {
  if (isSimulatableRole(simulatedRole)) return new Set([simulatedRole]);
  if (managesInstances) return new Set(["Admin"]);
  return new Set(realRoles);
}

/** How the simulated role reads in the chrome — "Viewing as Reader". */
export function simulationLabel(simulatedRole: string | null | undefined): string | null {
  return isSimulatableRole(simulatedRole) ? `Viewing as ${simulatedRole}` : null;
}
